import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, RotateCcw, UserRound } from 'lucide-react';
import { Chargement, Compteur, EnteteEcran, MessageErreur, Pagination, Pastille, PastilleSite } from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import { LienOrganisation } from '../components/repertoire/LienOrganisation';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { useValeurDifferee } from '../lib/useRecherche';
import { jourCourt } from '../lib/useFormateurs';
import {
  useApprenantsEntreprises,
  type ApprenantEnEntreprise,
  type EntrepriseAvecApprenants,
} from '../lib/useApprenantsEntreprises';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

interface Filtres {
  recherche: string;
  secteur: string;
  formation: string;
}

const SANS_FILTRE: Filtres = { recherche: '', secteur: '', formation: '' };

/**
 * Apprenants & entreprises (phase 5, TF-481) : les entreprises où sont
 * aujourd'hui les apprenants, par secteur d'activité, pour préparer les
 * visites en entreprise. Chaque entreprise mène à sa fiche du répertoire,
 * chaque apprenant à son dossier.
 *
 * L'écran ne décide rien : qui est dans quelle entreprise (contrat CFA en
 * vigueur, accord de contrat de professionnalisation OF), le regroupement des
 * écritures d'un même secteur et les compteurs viennent de
 * `/api/apprenants-entreprises`.
 */
export function DirApprenantsEntreprisesPage(): JSX.Element {
  const { site } = usePerimetre();
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const { data, isLoading, error } = useApprenantsEntreprises({ site, ...filtres, recherche });

  useEffect(() => setPage(1), [site, taillePage, recherche, filtres.secteur, filtres.formation]);

  const changer = <C extends keyof Filtres>(cle: C, valeur: Filtres[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const filtreActif = filtres.recherche !== '' || filtres.secteur !== '' || filtres.formation !== '';
  const entreprises = data?.entreprises ?? [];
  const affichees = entreprises.slice((page - 1) * taillePage, page * taillePage);
  const compteurs = data?.compteurs;
  const perimetreVide = data !== undefined && data.options.secteurs.length === 0;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Apprenants & entreprises"
        sousTitre="Entreprises où sont les apprenants aujourd’hui, par secteur · pour préparer les visites"
        actions={<BarreSites />}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur libelle="Entreprises" valeur={compteurs?.entreprises ?? '—'} ton="navy" detail="Accueillant au moins un apprenant" />
        <Compteur
          libelle="Apprenants en entreprise"
          valeur={compteurs?.apprenants ?? '—'}
          ton="mint"
          detail="Contrat en cours aujourd’hui"
        />
        <Compteur libelle="Secteurs d’activité" valeur={compteurs?.secteurs ?? '—'} ton="blue" detail="Représentés dans la liste" />
        <Compteur
          libelle="Secteur non renseigné"
          valeur={compteurs?.sansSecteur ?? '—'}
          ton={compteurs && compteurs.sansSecteur > 0 ? 'warm' : 'navy'}
          detail="Entreprises à compléter au répertoire"
        />
      </div>

      <div className="flex flex-col gap-2 rounded-2xl bg-white p-3 shadow-card sm:flex-row sm:flex-wrap sm:items-center sm:p-4">
        <ChampRecherche
          valeur={filtres.recherche}
          onChange={(valeur) => changer('recherche', valeur)}
          etiquette="Rechercher une entreprise ou un apprenant"
          placeholder="Entreprise, ville, apprenant…"
        />
        <ChampFiltre valeur={filtres.secteur} onChange={(valeur) => changer('secteur', valeur)} etiquette="Secteur d’activité">
          <option value="">Tous les secteurs</option>
          {data?.options.secteurs.map((secteur) => (
            <option key={secteur.cle} value={secteur.cle}>
              {secteur.libelle} ({secteur.entreprises})
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={filtres.formation} onChange={(valeur) => changer('formation', valeur)} etiquette="Formation">
          <option value="">Toutes les formations</option>
          {data?.options.formations.map((formation) => (
            <option key={formation.code} value={formation.code}>
              {formation.titre}
            </option>
          ))}
        </ChampFiltre>
        {filtreActif && (
          <button
            type="button"
            onClick={() => setFiltres(SANS_FILTRE)}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Réinitialiser
          </button>
        )}
      </div>

      {isLoading && <Chargement libelle="Chargement des entreprises…" />}
      {error && <MessageErreur message={messageErreur(error, 'La liste n’a pas pu être chargée.')} />}
      {!isLoading && !error && entreprises.length === 0 && (
        <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">
          {perimetreVide
            ? 'Aucun apprenant en entreprise aujourd’hui dans ce périmètre.'
            : 'Aucune entreprise ne correspond aux filtres.'}
        </p>
      )}

      {!isLoading && !error && entreprises.length > 0 && (
        <>
          <div className="space-y-4">
            {affichees.map((entreprise) => (
              <CarteEntreprise key={entreprise.id} entreprise={entreprise} />
            ))}
          </div>
          <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            <Pagination
              page={page}
              pages={Math.max(1, Math.ceil(entreprises.length / taillePage))}
              total={entreprises.length}
              affiches={affichees.length}
              taillePage={taillePage}
              onPage={setPage}
              onChangerTaille={setTaillePage}
              uniteLabel="entreprise"
            />
          </div>
        </>
      )}

      {data && (
        <p className="text-[11px] text-codialis-gray">
          Contrats en cours le {jourCourt(data.jour)} : contrats d’apprentissage et de professionnalisation des dossiers
          CFA — un contrat rompu compte jusqu’à la veille de la rupture —, accords de contrat de professionnalisation des
          parcours OF · antenne de l’apprenant · secteur tel que saisi au répertoire.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Une entreprise et ses apprenants
// ---------------------------------------------------------------------------

const CLASSE_LIEN = 'text-codialis-sky hover:underline';
const GRILLE_APPRENANT =
  'grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1.3fr)_minmax(0,1fr)] md:gap-4';

function Libelle({ children }: { children: ReactNode }): JSX.Element {
  // Lu par les lecteurs d'écran à toutes les largeurs ; vu seulement sans l'en-tête des colonnes.
  return (
    <span className="block text-[11px] font-semibold uppercase tracking-wider text-codialis-gray md:sr-only">
      {children}
    </span>
  );
}

function Detail({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-[11px] text-codialis-gray">{children}</div>;
}

function adresseComplete(entreprise: EntrepriseAvecApprenants): string | null {
  const commune = [entreprise.codePostal, entreprise.ville].filter(Boolean).join(' ');
  const ligne = [entreprise.adresse, commune].filter(Boolean).join(', ');
  return ligne || null;
}

function CarteEntreprise({ entreprise }: { entreprise: EntrepriseAvecApprenants }): JSX.Element {
  const adresse = adresseComplete(entreprise);
  return (
    <section
      aria-labelledby={`entreprise-${entreprise.id}`}
      className="overflow-hidden rounded-2xl bg-white shadow-card"
    >
      <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:p-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1.5">
          <h2 id={`entreprise-${entreprise.id}`} className="font-display text-base font-bold text-codialis-navy">
            <LienOrganisation
              id={entreprise.id}
              nom={entreprise.denomination}
              classeLien="hover:text-codialis-sky hover:underline [@media(pointer:coarse)]:inline-block [@media(pointer:coarse)]:min-h-[40px] [@media(pointer:coarse)]:leading-10"
            />
          </h2>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-codialis-gray">
            <Pastille texte={entreprise.secteur ?? 'Secteur non renseigné'} ton={entreprise.secteur ? 'info' : 'neutral'} />
            <span>{pluriel(entreprise.apprenants.length, 'apprenant')}</span>
          </div>
        </div>
        <ul className="min-w-0 space-y-1 text-xs text-codialis-navy lg:max-w-md lg:text-right">
          <li className="flex items-start gap-1.5 lg:justify-end">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-codialis-gray" aria-hidden />
            <span className="min-w-0">{adresse ?? 'Adresse non renseignée'}</span>
          </li>
          {entreprise.telephone && (
            <li className="flex items-center gap-1.5 lg:justify-end">
              <Phone className="h-3.5 w-3.5 shrink-0 text-codialis-gray" aria-hidden />
              <a href={`tel:${entreprise.telephone.replace(/\s/g, '')}`} className={`${CLASSE_LIEN} [@media(pointer:coarse)]:leading-10`}>
                {entreprise.telephone}
              </a>
            </li>
          )}
          {entreprise.email && (
            <li className="flex min-w-0 items-center gap-1.5 lg:justify-end">
              <Mail className="h-3.5 w-3.5 shrink-0 text-codialis-gray" aria-hidden />
              <a href={`mailto:${entreprise.email}`} className={`${CLASSE_LIEN} min-w-0 break-all [@media(pointer:coarse)]:leading-10`}>
                {entreprise.email}
              </a>
            </li>
          )}
          {entreprise.contactOperationnel && (
            <li className="flex items-center gap-1.5 lg:justify-end">
              <UserRound className="h-3.5 w-3.5 shrink-0 text-codialis-gray" aria-hidden />
              <span className="min-w-0">Contact : {entreprise.contactOperationnel}</span>
            </li>
          )}
        </ul>
      </div>

      <div aria-hidden className={`hidden border-b border-gray-100 bg-gray-50 px-5 py-2 md:grid ${GRILLE_APPRENANT}`}>
        {['Apprenant', 'Formation', 'Contrat', 'Tuteur'].map((colonne) => (
          <span key={colonne} className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            {colonne}
          </span>
        ))}
      </div>
      <ul className="divide-y divide-gray-100">
        {entreprise.apprenants.map((apprenant) => (
          <LigneApprenant key={apprenant.id} apprenant={apprenant} />
        ))}
      </ul>
    </section>
  );
}

function LigneApprenant({ apprenant }: { apprenant: ApprenantEnEntreprise }): JSX.Element {
  return (
    <li className={`px-4 py-3 text-xs sm:px-5 ${GRILLE_APPRENANT}`}>
      <div className="min-w-0">
        <Libelle>Apprenant</Libelle>
        <Link
          to={`/learners/${apprenant.id}`}
          className="block truncate text-sm font-semibold text-codialis-navy hover:text-codialis-sky hover:underline [@media(pointer:coarse)]:leading-10"
        >
          {apprenant.prenom} {apprenant.nom}
        </Link>
        <PastilleSite site={apprenant.siteCode} />
      </div>
      <div className="min-w-0">
        <Libelle>Formation</Libelle>
        <div className="font-medium text-codialis-navy">{apprenant.formation?.titre ?? 'Sans formation rattachée'}</div>
      </div>
      <div className="min-w-0 space-y-0.5">
        <Libelle>Contrat</Libelle>
        <div className="text-codialis-navy">
          {apprenant.libelleContrat}
          {apprenant.accord ? ' · parcours OF' : ''}
        </div>
        <Detail>
          {apprenant.fin ? `Du ${jourCourt(apprenant.debut)} au ${jourCourt(apprenant.fin)}` : `Depuis le ${jourCourt(apprenant.debut)}`}
        </Detail>
        {apprenant.dernierJour && <Pastille texte={`Rupture · dernier jour le ${jourCourt(apprenant.dernierJour)}`} ton="warning" />}
      </div>
      <div className="min-w-0 space-y-0.5">
        <Libelle>Tuteur</Libelle>
        {apprenant.tuteur ? (
          <>
            <div className="text-codialis-navy">
              {apprenant.tuteur.prenom} {apprenant.tuteur.nom}
            </div>
            {apprenant.tuteur.telephone && (
              <a
                href={`tel:${apprenant.tuteur.telephone.replace(/\s/g, '')}`}
                className={`block ${CLASSE_LIEN} [@media(pointer:coarse)]:leading-10`}
              >
                {apprenant.tuteur.telephone}
              </a>
            )}
          </>
        ) : (
          <Detail>{apprenant.accord ? 'Non suivi par l’accord' : 'Non renseigné'}</Detail>
        )}
      </div>
    </li>
  );
}
